/** Server-side pagination control compatible with the Bootstrap 3/5 scoped skin. */
import { useUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';

interface Props { page: number; pageSize: number; total: number; onChange: (page: number) => void }

export function Pagination({ page, pageSize, total, onChange }: Props) {
  const { locale, culture } = useUiLocale();
  const copy = publicCopy(locale);
  const localize = (value: number) => value.toLocaleString(culture, { useGrouping: false });
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  if (pageCount <= 1) return null;

  const currentPage = Math.min(pageCount, Math.max(1, page));
  const maxButtons = 5;
  const start = Math.max(1, Math.min(currentPage - 2, pageCount - maxButtons + 1));
  const end = Math.min(pageCount, start + maxButtons - 1);
  const pages = Array.from({ length: end - start + 1 }, (_, index) => start + index);

  return <nav className="df-pagination" aria-label={copy.pageNavigation}>
    <button type="button" disabled={currentPage <= 1} onClick={() => onChange(1)} aria-label={copy.firstPage}>«</button>
    <button type="button" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)}>{copy.previous}</button>
    {start > 1 ? <span className="df-pagination__ellipsis" aria-hidden="true">…</span> : null}
    {pages.map((item) => <button
      key={item}
      type="button"
      className={item === currentPage ? 'active' : ''}
      aria-current={item === currentPage ? 'page' : undefined}
      aria-label={copy.page(localize(item))}
      onClick={() => onChange(item)}
    >{localize(item)}</button>)}
    {end < pageCount ? <span className="df-pagination__ellipsis" aria-hidden="true">…</span> : null}
    <button type="button" disabled={currentPage >= pageCount} onClick={() => onChange(currentPage + 1)}>{copy.next}</button>
    <button type="button" disabled={currentPage >= pageCount} onClick={() => onChange(pageCount)} aria-label={copy.lastPage}>»</button>
  </nav>;
}
